/**
 * Não conformidades no portal. Quem tem a capacidade de tratar muda o estado
 * daqui mesmo; quem não tem, só consulta — a API recusa de qualquer forma.
 */
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MaintenanceNonConformityDto, MaintenanceNonConformityStatusDto } from "@bi/types";
import { api, ApiError } from "../../lib/api";
import { EmptyState } from "../../components/EmptyState";
import { IconShield } from "../../components/icons";
import { CriticalityBadge, NC_STATUS } from "../../features/maintenance/labels";
import { portalPaths } from "../../features/portal/portal-nav";
import { useCapabilities } from "../../features/maintenance/useCapabilities";
import { EvidenceThumbs } from "../../features/maintenance/EvidenceThumbs";

const FILTROS: Array<{ value: string; label: string }> = [
  { value: "open", label: "Abertas" },
  { value: "in_analysis", label: "Em análise" },
  { value: "resolved", label: "Resolvidas" },
  { value: "", label: "Todas" },
];

const PROXIMO: Partial<Record<MaintenanceNonConformityStatusDto, { para: MaintenanceNonConformityStatusDto; label: string }>> = {
  open: { para: "in_analysis", label: "Colocar em análise" },
  in_analysis: { para: "resolved", label: "Marcar como resolvida" },
};

export function PortalOccurrencesPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "open";
  const plate = params.get("plate") ?? "";
  const qc = useQueryClient();
  const { can } = useCapabilities();
  const [erro, setErro] = useState<string | null>(null);

  const lista = useQuery({
    queryKey: ["maintenance", "ncs", status, plate],
    queryFn: () => {
      const q = new URLSearchParams({ limit: "50" });
      if (status) q.set("status", status);
      if (plate) q.set("plate", plate);
      return api.get<{ rows: MaintenanceNonConformityDto[]; total: number }>(`/maintenance/non-conformities?${q}`);
    },
  });

  const mudar = useMutation({
    mutationFn: (v: { id: string; status: MaintenanceNonConformityStatusDto }) =>
      api.patch(`/maintenance/non-conformities/${v.id}`, { status: v.status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["maintenance"] }),
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível mudar a ocorrência."),
  });

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold text-gray-900">Não Conformidades</h1>

      <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
        {FILTROS.map((f) => (
          <button
            key={f.label}
            onClick={() => { const p = new URLSearchParams(params); if (f.value) p.set("status", f.value); else p.delete("status"); setParams(p, { replace: true }); }}
            className={`flex-shrink-0 min-h-[40px] px-3 rounded-lg border text-sm ${
              status === f.value ? "bg-tm-500 text-white border-tm-500" : "bg-white text-gray-600 border-surface-border"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {erro && <div className="card p-3 border-danger-600/30 bg-danger-50 text-sm text-danger-600">{erro}</div>}

      {lista.isLoading ? (
        <p className="text-sm text-gray-500">Carregando…</p>
      ) : (lista.data?.rows.length ?? 0) === 0 ? (
        <div className="card">
          <EmptyState icon={<IconShield className="w-6 h-6" />} title="Nenhuma ocorrência"
            description="As não conformidades apontadas nos checklists aparecem aqui." />
        </div>
      ) : (
        <div className="space-y-3">
          {lista.data!.rows.map((nc) => {
            const proximo = PROXIMO[nc.status];
            return (
              <article key={nc.id} className="card p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900">{nc.itemLabel}{nc.defectLabel ? ` — ${nc.defectLabel}` : ""}</p>
                    <p className="text-xs text-gray-500">
                      <Link className="font-mono text-tm-700 hover:underline" to={portalPaths.vehicle(nc.vehiclePlate)}>{nc.vehiclePlate}</Link>
                      {nc.trailerPlate && <span className="font-mono"> + {nc.trailerPlate}</span>}
                      {nc.driverName ? ` · ${nc.driverName}` : ""}
                      {` · ${new Date(nc.openedAt).toLocaleDateString("pt-BR")}`}
                    </p>
                    <EvidenceThumbs anexos={nc.attachments} size="sm" />
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <CriticalityBadge value={nc.criticality} />
                    <span className={NC_STATUS[nc.status].badge}>{NC_STATUS[nc.status].label}</span>
                  </div>
                </div>
                {nc.description && <p className="text-sm text-gray-600">{nc.description}</p>}
                {nc.tire && (
                  <p className="text-xs text-gray-700 mt-0.5">
                    Pneu <Link className="font-mono text-tm-700 hover:underline" to={`/frota/pneus/${nc.tire.id}`}>{nc.tire.code}</Link>
                    {nc.tirePosition ? ` · ${nc.tirePosition.label}` : ""}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <Link className="btn-ghost min-h-[40px]" to={portalPaths.run(nc.runId)}>Ver checklist</Link>
                  {can("nonconformity.treat") && proximo && (
                    <button
                      className="btn-secondary min-h-[40px]"
                      disabled={mudar.isPending}
                      onClick={() => { setErro(null); mudar.mutate({ id: nc.id, status: proximo.para }); }}
                    >
                      {proximo.label}
                    </button>
                  )}
                  {nc.workOrder && (
                    <Link className="badge-blue hover:underline" to={portalPaths.workOrder(nc.workOrder.id)}>O.S. nº {nc.workOrder.number}</Link>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
